import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiCalendar,
  FiDollarSign,
  FiUser,
  FiMail,
  FiCheck,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import { API_BASE_URL, toastdisplay } from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import './CoachingPaymentSuccess.css';

/**
 * CoachingPaymentSuccess Component
 *
 * Handles payment verification for coaching sessions with two different URL patterns:
 *
 * 1. Legacy pattern: /student/coaching/booking-success?booking_id=123&session_id=sess_xyz
 *    - Uses existing booking record for verification
 *    - API: POST /api/student/bookings/{booking_id}/verify-payment
 *
 * 2. New pattern: /student/coaching/booking-success?service_id=8&session_id=sess_xyz
 *    - Creates/finds booking based on service and user
 *    - API: POST /api/student/coaching/verify-payment
 *
 * Expected API Responses:
 *
 * Both endpoints should return:
 * {
 *   payment_verified: boolean,
 *   message?: string,
 *   booking: {
 *     id?: number,
 *     service_title?: string,
 *     booking_date?: string (YYYY-MM-DD),
 *     start_time?: string (HH:MM),
 *     end_time?: string (HH:MM),
 *     price_paid?: number,
 *     duration?: number
 *   }
 * }
 *
 * On successful verification, the component:
 * - Updates booking status to 'validated'
 * - Shows success message with booking details
 * - Provides next steps information
 *
 * On failed verification:
 * - Shows error message
 * - Provides retry/support options
 */
const CoachingPaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [user] = useState(() => getStoredUser());
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  // Email validation state
  const [emailValidated, setEmailValidated] = useState(false);

  useEffect(() => {
    const verifyCoachingPayment = async () => {
      const bookingId = searchParams.get('booking_id');
      const serviceId = searchParams.get('service_id');
      const studentEmail = searchParams.get('student_email');

      // Debug logging
      console.log('CoachingPaymentSuccess - URL params:', {
        bookingId,
        serviceId,
        studentEmail,
      });

      // Handle both URL patterns: booking_id or service_id
      if (!bookingId && !serviceId) {
        console.error('No booking_id or service_id found in URL');
        setPaymentStatus('failed');
        setLoading(false);
        return;
      }

      try {
        let response;
        let apiEndpoint;

        apiEndpoint = `${API_BASE_URL}/api/student/coaching/verify-payment`;
        console.log('Using service_id flow:', apiEndpoint);

        response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            service_id: serviceId,
            booking_id: bookingId,
            student_email: studentEmail,
          }),
        });

        console.log('Payment verification response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('Payment verification result:', result);

          if (result.payment_verified) {
            setPaymentStatus('success');
            setBookingDetails(result.booking);

            // Update booking status to 'validated' using the booking ID from result
            const successMessage =
              'Payment successful! Your coaching session has been confirmed.';

            toastdisplay(successMessage, true, 3000, setToast);
          } else {
            console.warn('Payment verification failed:', result);
            setPaymentStatus('failed');
            toastdisplay(
              result.message ||
                'Payment verification failed. Please contact support at contact@liwaza.org',
              false,
              3000,
              setToast
            );
          }
        } else {
          console.error('Payment verification API error:', response.status);
          const errorResult = await response.json().catch(() => ({}));
          console.error('Error details:', errorResult);

          setPaymentStatus('failed');
          toastdisplay(
            errorResult.message ||
              'Payment verification failed. Please try again.',
            false,
            3000,
            setToast
          );
        }
      } catch (error) {
        console.error('Error verifying coaching payment:', error);
        setPaymentStatus('failed');
        toastdisplay(
          'Payment verification failed due to network error. Please contact support.',
          false,
          3000,
          setToast
        );
      }

      setLoading(false);
    };

    verifyCoachingPayment();
  }, [searchParams, user?.email]);

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/bookings/${bookingId}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: status,
            student_email: user?.email,
          }),
        }
      );

      if (!response.ok) {
        console.error('Failed to update booking status');
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const renderPaymentSuccess = () => (
    <div className="coaching-payment-content">
      <div className="success-header">
        <div className="success-icon">
          <FiCheckCircle size={80} />
        </div>
        <h1>Coaching Session Booked!</h1>
        <p className="success-subtitle">
          Your payment has been processed successfully. Your coaching session is
          confirmed!
        </p>
      </div>

      {bookingDetails && (
        <div className="booking-summary-card">
          <div className="booking-summary-header">
            <h3>Booking Confirmation</h3>
            <span className="booking-id">
              {bookingDetails.id
                ? `Booking #${bookingDetails.id}`
                : 'Booking Confirmed'}
            </span>
          </div>

          <div className="booking-details-grid">
            {bookingDetails.service_title && (
              <div className="detail-card">
                <div className="detail-icon">
                  <FiUser size={24} />
                </div>
                <div className="detail-content">
                  <h4>Service</h4>
                  <p>{bookingDetails.service_title}</p>
                </div>
              </div>
            )}

            {bookingDetails.booking_date && bookingDetails.start_time && (
              <div className="detail-card">
                <div className="detail-icon">
                  <FiCalendar size={24} />
                </div>
                <div className="detail-content">
                  <h4>Date & Time</h4>
                  <p>{formatDate(bookingDetails.booking_date)}</p>
                  <small>
                    {formatTime(bookingDetails.start_time)} -{' '}
                    {formatTime(bookingDetails.end_time)}
                  </small>
                </div>
              </div>
            )}

            {bookingDetails.price_paid && (
              <div className="detail-card">
                <div className="detail-icon">
                  <FiDollarSign size={24} />
                </div>
                <div className="detail-content">
                  <h4>Amount Paid</h4>
                  <p>€{bookingDetails.price_paid}</p>
                  {bookingDetails.duration && (
                    <small>{bookingDetails.duration} minutes session</small>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!bookingDetails && (
        <div className="booking-summary-card">
          <div className="booking-summary-header">
            <h3>Payment Confirmed</h3>
          </div>
          <p
            style={{
              textAlign: 'center',
              padding: '1rem',
              color: 'var(--liwaza-on-surface-variant)',
            }}
          >
            Your payment has been successfully processed. Detailed booking
            information will be sent to your email shortly.
          </p>
        </div>
      )}

      {/* Email Validation Section */}
      <div className="email-validation-section">
        <div
          className={`email-validation-card ${emailValidated ? 'validated' : ''}`}
        >
          <div className="email-validation-header">
            {emailValidated ? (
              <FiCheckCircle
                className="email-validation-icon success"
                size={40}
              />
            ) : (
              <FiMail className="email-validation-icon" size={40} />
            )}
            <h3>
              {emailValidated
                ? 'Email Confirmed!'
                : 'Confirm Your Email Invitation'}
            </h3>
          </div>

          {emailValidated ? (
            <div className="email-validation-success">
              <p>
                Thank you for confirming! Your session is now fully set up. We
                look forward to seeing you at your coaching session.
              </p>
            </div>
          ) : (
            <>
              <div className="email-validation-instructions">
                <p>
                  <strong>Important:</strong> Please check your email (including
                  spam/junk folder) for the calendar invitation from your coach.
                </p>
                <ul>
                  <li>
                    Look for an email with your meeting details and calendar
                    invite
                  </li>
                  <li>
                    Accept the calendar invitation to add it to your calendar
                  </li>
                  <li>
                    If you don't receive it within 24 hours, contact your coach
                  </li>
                </ul>
              </div>

              <div className="email-validation-alert">
                <FiAlertCircle size={18} />
                <span>
                  The invitation may take a few minutes to arrive. Please check
                  both your inbox and spam folder.
                </span>
              </div>

              <button
                className="btn-email-validate"
                onClick={() => {
                  setEmailValidated(true);
                  // Store validation in localStorage
                  const validatedBookings = JSON.parse(
                    localStorage.getItem('liwaza_validated_bookings') || '[]'
                  );
                  validatedBookings.push({
                    bookingId: bookingDetails?.id,
                    serviceTitle: bookingDetails?.service_title,
                    validatedAt: new Date().toISOString(),
                  });
                  localStorage.setItem(
                    'liwaza_validated_bookings',
                    JSON.stringify(validatedBookings)
                  );
                  toastdisplay(
                    'Email confirmation recorded!',
                    true,
                    2000,
                    setToast
                  );
                }}
              >
                <FiCheck size={18} />I received the email invitation
              </button>
            </>
          )}
        </div>
      </div>

      <div className="next-steps-section">
        <h3>What happens next?</h3>
        <div className="steps-list">
          <div className="step-item">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Coach Notification</h4>
              <p>
                Your coach has been automatically notified of your booking and
                will receive all session details.
              </p>
            </div>
          </div>

          <div className="step-item">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Session Details</h4>
              <p>
                You'll receive complete session details and meeting link via
                email within 24 hours. Check your email regularly.
              </p>
            </div>
          </div>

          <div className="step-item">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Session Preparation</h4>
              <p>
                Prepare any questions or materials you'd like to discuss during
                your coaching session.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="action-section">
        <div className="primary-actions">
          <button
            className="btn-coaching-primary"
            onClick={() => navigate('/coaching')}
          >
            Go to Dashboard
          </button>
          <button
            className="btn-coaching-secondary"
            onClick={() => navigate('/coaching')}
          >
            Browse More Coaches
          </button>
        </div>

        <div className="support-info">
          <p>
            Need help? Contact us at{' '}
            <a href="mailto:contact@liwaza.org">contact@liwaza.org</a>
          </p>
        </div>
      </div>
    </div>
  );

  const renderPaymentFailed = () => (
    <div className="coaching-payment-content">
      <div className="error-header">
        <div className="error-icon">
          <FiAlertCircle size={80} />
        </div>
        <h1>Payment Failed</h1>
        <p className="error-subtitle">
          There was an issue processing your payment. Your coaching session has
          not been confirmed.
        </p>
      </div>

      <div className="error-details">
        <h3>What went wrong?</h3>
        <ul>
          <li>Payment could not be processed by your bank</li>
          <li>Session verification failed</li>
          <li>Connection timeout during payment</li>
        </ul>
      </div>

      <div className="action-section">
        <div className="primary-actions">
          <button
            className="btn-coaching-primary"
            onClick={() => navigate('/coaching')}
          >
            Try Booking Again
          </button>
          <button
            className="btn-coaching-secondary"
            onClick={() => navigate('/community')}
          >
            Go to Dashboard
          </button>
        </div>

        <div className="support-info">
          <p>
            Still having issues? Contact our support team at{' '}
            <a href="mailto:support@liwaza.com">support@liwaza.com</a>
          </p>
        </div>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="coaching-payment-content">
      <div className="loading-header">
        <div className="loading-icon">
          <FiClock size={80} />
        </div>
        <h1>Verifying Payment...</h1>
        <p className="loading-subtitle">
          Please wait while we confirm your payment and coaching session
          details.
        </p>
      </div>

      <div className="loading-progress">
        <div className="progress-bar">
          <div className="progress-fill"></div>
        </div>
        <p>This usually takes just a few seconds...</p>
      </div>
    </div>
  );

  return (
    <div className="app">
      <Sidebar />
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span>{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}
      <main className="content">
        <div className="coaching-payment-success">
          {loading && renderLoading()}
          {!loading && paymentStatus === 'success' && renderPaymentSuccess()}
          {!loading && paymentStatus === 'failed' && renderPaymentFailed()}
        </div>
      </main>
    </div>
  );
};

export default CoachingPaymentSuccess;
